import { zodResolver } from '@hookform/resolvers/zod';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FieldErrors } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useNovel } from '@/context/novel-context';
import { CharacterFormSchema, type CharacterDTO } from '@/features/characters';
import { LocationFormSchema, type LocationDTO } from '@/features/locations';
import { OrganizationFormSchema, type OrganizationDTO } from '@/features/organizations';
import { useEntitySettings } from '@/features/shared/hooks/use-entity-settings';
import { useImageProcessor } from '@/features/shared/hooks/use-image-processor';
import { useFirebase } from '@/firebase';
import { useEntity } from '@/hooks/use-entity';
import { useToast } from '@/hooks/use-toast';
import type { Entity, EntityEventChange, EventEntityChange, FieldChange, PrimaryEntityType, ProcessedImage } from '@/lib/types';
import { applyDeltas } from '@/lib/utils/apply-deltas';
import { generateReciprocalDeltas, type ReciprocalDeltaPayload } from '@/lib/utils/generate-reciprocal-deltas';

import { mapCharacterDTOToDomain } from '@/features/characters/api/mappers';
import { mapLocationDTOToDomain } from '@/features/locations/api/mappers';
import { mapOrganizationDTOToDomain } from '@/features/organizations/api/mappers';

import { CollapsedCard } from './collapsed-card';
import { computeFieldDeltas, scrubChanges } from './compute-field-deltas';
import { EntityFormFields, EntityImageSection, type ImageManagerUpdate } from './entity-form-sections';

export type EventEntityChangeWithFailure = EventEntityChange & { failedToSave?: boolean };

interface EventChangeCardProps {
  eventId: string;
  eventName: string;
  eventOrder: number;
  entityId: string;
  entityType: PrimaryEntityType;
  entityName: string;
  existingChange?: EventEntityChangeWithFailure;
  expanded: boolean;
  onToggle: () => void;
  onSaveOverride?: (
    entityId: string,
    entityType: PrimaryEntityType,
    entityName: string,
    changes: FieldChange[],
    reciprocalChanges: ReciprocalDeltaPayload[],
    description: string,
  ) => Promise<void>;
  readOnly?: boolean;
}

const SCHEMA_BY_TYPE = {
  characters: CharacterFormSchema.passthrough(),
  locations: LocationFormSchema.passthrough(),
  organizations: OrganizationFormSchema.passthrough(),
} as const;

export function EventChangeCard({
  eventId, eventName, eventOrder,
  entityId, entityType, entityName,
  existingChange, expanded, onToggle, onSaveOverride, readOnly,
}: EventChangeCardProps) {
  const { t } = useTranslation();
  const { novel } = useNovel();
  const { backend } = useFirebase();
  const { toast } = useToast();

  const cardRef = useRef<HTMLDivElement>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [baselineState, setBaselineState] = useState<Entity | null>(null);
  const [timeTravelState, setTimeTravelState] = useState<Entity | null>(null);

  const { processImages, isProcessing: isProcessingImages } = useImageProcessor();
  const { sections: customSettings } = useEntitySettings(entityType);

  // Scroll into view on expand
  useEffect(() => {
    if (expanded && cardRef.current) {
      setTimeout(() => cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    }
  }, [expanded]);

  const { data: charData, isLoading: charLoading } = useEntity<CharacterDTO>('characters', entityType === 'characters' ? entityId : undefined);
  const { data: locData, isLoading: locLoading } = useEntity<LocationDTO>('locations', entityType === 'locations' ? entityId : undefined);
  const { data: orgData, isLoading: orgLoading } = useEntity<OrganizationDTO>('organizations', entityType === 'organizations' ? entityId : undefined);

  const isLoading = charLoading || locLoading || orgLoading;

  const baseData = useMemo<Entity | null>(() => {
    if (entityType === 'characters' && charData) {
      return { ...mapCharacterDTOToDomain(charData), entityType: 'characters' };
    }
    if (entityType === 'locations' && locData) {
      return { ...mapLocationDTOToDomain(locData), entityType: 'locations' };
    }
    if (entityType === 'organizations' && orgData) {
      return { ...mapOrganizationDTOToDomain(orgData), entityType: 'organizations' };
    }
    return null;
  }, [entityType, charData, locData, orgData]);

  const methods = useForm({
    resolver: zodResolver(SCHEMA_BY_TYPE[entityType] as z.ZodType<Record<string, unknown>>),
    defaultValues: (timeTravelState ?? {}) as Record<string, unknown>,
  });

  const { watch, setValue } = methods;
  const watchedImages = (watch('images') ?? []) as ProcessedImage[];
  const watchedPendingFiles = (watch('pendingFiles') ?? []) as File[];
  const watchedPrimaryImageId = (watch('primaryImageId') ?? null) as string | null;

  // Compute time-travel state from base data + prior event changes
  useEffect(() => {
    if (!baseData) return;

    let reconstructed: Entity = structuredClone(baseData);
    const allChanges = (baseData.eventChanges ?? []) as EntityEventChange[];
    const priorChanges = allChanges
      .map(ec => ({
        ...ec,
        _resolvedOrder: novel.summaries.events[ec.eventId]?.order ?? -1
      }))
      .filter(ec => ec._resolvedOrder < eventOrder || (ec._resolvedOrder === eventOrder && ec.eventId !== eventId))
      .sort((a, b) => a._resolvedOrder - b._resolvedOrder);

    for (const ec of priorChanges) {
      reconstructed = applyDeltas(reconstructed, ec.changes);
    }

    setBaselineState(reconstructed);

    let formState = structuredClone(reconstructed);
    if (existingChange) {
      formState = applyDeltas(formState, existingChange.changes);
    }

    setTimeTravelState(formState);
    methods.reset(formState as Record<string, unknown>);
  }, [baseData, eventOrder, eventId, existingChange, methods]);

  // Highlight fields that already have changes
  const changedFieldsStyles = useMemo(() => {
    if (!existingChange?.changes?.length) return {};
    const styles: Record<string, Record<string, string | Record<string, string>>> = {};
    for (const change of existingChange.changes) {
      const f = change.field;
      styles[`& .MuiInputBase-root:has([name="${f}"])`] = {
        backgroundColor: 'rgba(217, 119, 6, 0.08) !important',
        '&.MuiFilledInput-root': { borderBottom: '2px solid #d97706 !important' },
        '&.MuiInput-root': { borderBottom: '2px solid #d97706 !important' },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#d97706 !important',
          borderWidth: '2px !important',
        },
      };
      styles[`& [data-field~="${f}"]`] = {
        border: '2px solid #d97706 !important',
        backgroundColor: 'rgba(217, 119, 6, 0.05) !important',
        borderRadius: '4px',
      };
    }
    return styles;
  }, [existingChange]);

  const handleImageUpdate = useCallback((updates: ImageManagerUpdate) => {
    const images = updates.images ?? (methods.getValues('images') as ProcessedImage[] | undefined) ?? [];
    if (updates.images) setValue('images', images, { shouldDirty: true });
    if (updates.pendingFiles) setValue('pendingFiles', updates.pendingFiles, { shouldDirty: true });
    if (updates.primaryImageId !== undefined) {
      const selected = images.find(image => image.id === updates.primaryImageId);
      setValue('primaryImageId', updates.primaryImageId, { shouldDirty: true });
      setValue('mainImageUrl', selected?.profile?.url ?? selected?.original?.url ?? '', { shouldDirty: true });
      setValue('mainImagePath', selected?.profile?.path ?? selected?.original?.path ?? '', { shouldDirty: true });
    }
  }, [methods, setValue]);

  const handleInvalid = (errors: FieldErrors) => {
    console.error('[EventChangeCard] Form validation failed:', errors);
    const details = Object.entries(errors)
      .map(([key, err]) => `${key}: ${err?.message ?? 'invalid'}`)
      .join(', ');
    toast({ title: t('event_implications.validation_error'), description: details, variant: 'destructive' });
  };

  const handleSave = async (values: Record<string, unknown>) => {
    if (!novel.id || !backend) return;
    setIsSaving(true);

    try {
      const finalValues = await processFormImages(values);
      const baseline = (baselineState ?? timeTravelState ?? {}) as Record<string, never>;
      const merged = mergeFormSources(finalValues, methods.getValues() as Record<string, unknown>, baseline);
      const existingFields = existingChange?.changes?.map(c => c.field) ?? [];
      const deltas = scrubChanges(computeFieldDeltas(baseline, merged as Record<string, never>, existingFields));

      const description = existingChange?.description ?? '';

      const newReciprocals = generateReciprocalDeltas(entityType, entityId, deltas);

      if (deltas.length === 0) {
        if (onSaveOverride) {
          await onSaveOverride(entityId, entityType, entityName, [], newReciprocals, description);
          toast({ title: t('event_implications.save_success') });
          onToggle();
          return;
        }
        await handleEmptyDeltas();
        return;
      }

      if (onSaveOverride) {
        await onSaveOverride(entityId, entityType, entityName, deltas, newReciprocals, description);
        toast({ title: t('event_implications.save_success') });
        onToggle();
        return;
      }

      await saveDirectly(deltas, description, newReciprocals);
      toast({ title: t('event_implications.save_success') });
      onToggle();
    } catch (error) {
      console.error('Failed to save event changes:', error);
      toast({ title: t('event_implications.save_failed'), variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  // --- Private helpers -------------------------------------------------------

  async function processFormImages(values: Record<string, unknown>): Promise<Record<string, unknown>> {
    const result = { ...values };
    const pendingFiles = result['pendingFiles'] as File[] | undefined;
    delete result['pendingFiles'];

    const images = [...((result['images'] as ProcessedImage[]) ?? [])];
    if (pendingFiles?.length && novel) {
      const processed = await processImages(pendingFiles, novel.id, `${entityType}/${entityId}`, novel.ownerId);
      images.push(...processed);
      result['images'] = images;
    }
    const selectedId = typeof result['primaryImageId'] === 'string' ? result['primaryImageId'] : undefined;
    const selectedById = selectedId ? images.find(image => image.id === selectedId) : undefined;
    const main = selectedById ?? (selectedId ? undefined : images[0]);
    if (main?.id) result['primaryImageId'] = main.id;
    result['mainImageUrl'] = main?.profile?.url ?? main?.original?.url ?? '';
    result['mainImagePath'] = main?.profile?.path ?? main?.original?.path ?? '';
    return result;
  }

  function mergeFormSources(
    submitted: Record<string, unknown>,
    formState: Record<string, unknown>,
    baseline: Record<string, unknown>,
  ): Record<string, unknown> {
    const allKeys = new Set([...Object.keys(baseline), ...Object.keys(formState), ...Object.keys(submitted)]);
    const merged: Record<string, unknown> = {};
    for (const key of allKeys) {
      merged[key] = submitted[key] !== undefined ? submitted[key] : formState[key];
    }
    return merged;
  }

  async function handleEmptyDeltas(): Promise<void> {
    if (existingChange && novel && backend) {
      await backend.entities.deleteEventImplication(novel.ownerId, novel, eventId, entityType, entityId);
      toast({ title: t('event_implications.save_success') });
      onToggle();
    } else {
      setIsSaving(false);
      toast({ title: t('event_implications.no_changes_detected') });
    }
  }

  async function saveDirectly(deltas: FieldChange[], description: string, reciprocals: ReciprocalDeltaPayload[]): Promise<void> {
    if (!novel.id || !backend) return;
    await backend.entities.saveEventImplication(
      novel.ownerId, novel, eventId, eventName, eventOrder,
      entityType, entityId, entityName, deltas, description, !!existingChange,
    );
  }

  // --- Render ----------------------------------------------------------------

  const displayImage = timeTravelState?.mainImageUrl ?? baseData?.mainImageUrl;
  const displayPath = timeTravelState?.mainImagePath ?? baseData?.mainImagePath;

  if (!expanded) {
    return (
      <CollapsedCard
        entityName={entityName}
        entityType={entityType}
        existingChange={existingChange}
        displayImage={displayImage}
        displayPath={displayPath}
        onToggle={onToggle}
        t={t}
      />
    );
  }

  return (
    <Card ref={cardRef} sx={{ width: '100%', borderRadius: 3, border: '1px solid', borderColor: existingChange?.failedToSave ? 'error.main' : 'primary.main', mb: 2 }}>
      <CardHeader
        title={<Typography variant="h6">{entityName}</Typography>}
        subheader={<Typography variant="caption" sx={{ textTransform: 'capitalize' }}>{entityType}</Typography>}
        action={<IconButton onClick={onToggle}><CloseIcon /></IconButton>}
        sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}
      />
      <CardContent sx={{ bgcolor: 'background.default', p: { xs: 2, md: 4 } }}>
        {isLoading || !timeTravelState ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <FormProvider {...methods}>
            <Box sx={changedFieldsStyles}>
              <fieldset disabled={readOnly} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
                <Grid container spacing={4}>
                  <Grid size={{ xs: 12, lg: 8 }}>
                    <Stack spacing={4}>
                      <EntityFormFields
                        entityType={entityType}
                        entityId={entityId}
                        eventId={eventId}
                        novelId={novel?.id ?? ''}
                        customSettings={customSettings}
                        isSaving={isSaving}
                      />
                      {!readOnly && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 4 }}>
                          <Button
                            type="button"
                            onClick={methods.handleSubmit(handleSave, handleInvalid)}
                            variant="contained"
                            disabled={isSaving}
                            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
                          >
                            {t('event_implications.save_implication')}
                          </Button>
                        </Box>
                      )}
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, lg: 4 }}>
                    <Stack spacing={4}>
                      <Box data-field="images primaryImageId pendingFiles">
                        <EntityImageSection
                          entityType={entityType}
                          entityId={entityId}
                          timeTravelState={timeTravelState}
                          watchedImages={watchedImages}
                          watchedPendingFiles={watchedPendingFiles}
                          watchedPrimaryImageId={watchedPrimaryImageId}
                          isProcessingImages={isProcessingImages}
                          watch={watch}
                          onUpdate={handleImageUpdate}
                          readOnly={readOnly}
                        />
                      </Box>
                    </Stack>
                  </Grid>
                </Grid>
              </fieldset>
            </Box>
          </FormProvider>
        )}
      </CardContent>
    </Card>
  );
}
